import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from '@api/common/services/api-helper.service';
import { ApiUrlFactory } from '@api/common/factories/api-url.factory';
import { CollectionRequest } from '@api/common/requests/collection.request';
import { map, Observable } from 'rxjs';
import { CollectionResponse } from '@api/common/responses/collection.response';
import { Id } from '@shared/types/id';
import { ApiParams } from '@environments/enums/api-params';
import { SequenceFilterCollectionRequest } from '@api/sequence/filters/sequence-filter-collection.request';
import { SequenceCollectionItemResponse } from '@api/sequence/responses/sequence-collection-item.response';
import { SequenceDetailsResponse } from '@api/sequence/responses/sequence-details.response';
import { environment } from '@environments/environment';
import { CreateResourceResponse } from '@api/common/responses/create-recource.response';
import { SequenceCreateRequest } from '@api/sequence/requests/sequence-create.request';

const { SEQUENCES } = environment.api;

@Injectable({
  providedIn: 'root',
})
export class SequenceApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly helper: ApiHelperService,
    private readonly urlFactory: ApiUrlFactory
  ) {}

  collection(
    request: CollectionRequest<SequenceFilterCollectionRequest>
  ): Observable<CollectionResponse<SequenceCollectionItemResponse>> {
    const { LIST, HOST } = SEQUENCES;
    const url = this.urlFactory.withQueryParams(
      HOST,
      LIST,
      this.helper.collectionRequestToQueryParams({
        request,
        filterRequestType: SequenceFilterCollectionRequest,
      })
    );

    return this.http
      .get(url)
      .pipe(
        map(
          this.helper.bodyToCollectionResponse(SequenceCollectionItemResponse)
        )
      );
  }

  details(id: Id): Observable<SequenceDetailsResponse> {
    const { DETAILS, HOST } = SEQUENCES;
    const url = this.urlFactory.withParams(HOST, DETAILS, {
      [ApiParams.SequenceId]: String(id),
    });

    return this.http
      .get(url)
      .pipe(map(this.helper.bodyToResponse(SequenceDetailsResponse)));
  }

  create(request: SequenceCreateRequest): Observable<CreateResourceResponse> {
    const { CREATE, HOST } = SEQUENCES;
    const url = this.urlFactory.simple(HOST, CREATE);
    const body = this.helper.requestToBody(request, SequenceCreateRequest);

    return this.http
      .post(url, body)
      .pipe(map(this.helper.bodyToResponse(CreateResourceResponse)));
  }

  remove(id: Id): Observable<void> {
    const { REMOVE, HOST } = SEQUENCES;
    const url = this.urlFactory.withParams(HOST, REMOVE, {
      [ApiParams.SequenceId]: String(id),
    });
    const body = this.helper.emptyBody();

    return this.http.delete<void>(url, body);
  }
}
